import React from "react";
import { X } from "@phosphor-icons/react";

/**
 * Iter 19.63.4 — Detail-Modal für die Gesamtsummen-Berechnung.
 * Zeigt pro anrechenbarer Zeile den exakten 30/360-Rechenweg
 * (Raw-Y/M/T × Faktor → kaufmännische Tage) und kaskadiert am Ende
 * den Gesamt-Total auf Y/M/T zurück.
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   rows: Array<row>  // aus StepThree (mit dauer_jahre/monate/tage + faktor + anrechenbar)
 *   aggregate: { display, jahre, monate, tage, totalDays }
 *   capMonths: number | undefined
 *   stichtag: string (ISO)
 */
const _fmtDE = (n) => {
  if (typeof n !== "number" || !Number.isFinite(n)) return String(n ?? "");
  if (Number.isInteger(n)) return String(n);
  const r = Math.round(n * 10) / 10;
  if (Number.isInteger(r)) return String(r);
  return String(r).replace(".", ",");
};

const _fmtDate = (iso, wasLaufend = false) => {
  if (!iso || typeof iso !== "string") {
    // Iter 19.63.22 — Wenn was_laufend gesetzt ist UND iso leer/falsy,
    // dann „laufend" wörtlich rendern (statt em-dash). Konsistenz mit
    // Step-3-Tabelle.
    if (wasLaufend) return "laufend";
    return "—";
  }
  const lc = iso.toLowerCase();
  if (lc === "laufend" || lc === "offen") return "laufend";
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso;
  return `${m[3]}.${m[2]}.${m[1]}`;
};

// Iter 19.63.11 — Format months as "1 J. 0 M." or "1 J. 6 M." etc. for the
// Ausbildungs-Bonus-Spalte. Pure J./M. — kein Tage-Teil (Months are integers).
const _fmtMonthsYM = (months) => {
  const m = Math.max(0, Math.round(Number(months) || 0));
  if (m === 0) return "0 M.";
  const y = Math.floor(m / 12);
  const mm = m % 12;
  const parts = [];
  if (y) parts.push(`${y} J.`);
  if (mm || !y) parts.push(`${mm} M.`);
  return parts.join(" ");
};

export default function BerechnungsdetailsModal({
  open,
  onClose,
  rows = [],
  ausbildungszeiten = [],
  aggregate = null,
  capMonths,
  stichtag,
  // Iter 19.63.103 — Cap-Bypass-Info-Sektion (optional).
  // Wenn Bypass-Items vorhanden sind, zeigt das Modal eine separate Sektion
  // mit Liste, Lücken-Analyse und KV-Regel-Hinweis.
  capBypassRules = null,
  bypassVordienstTotalDays = 0,
}) {
  if (!open) return null;

  // Filter only anrechenbar rows for the trace; non-anr rows contribute 0.
  const anrechenbareRows = (rows || []).filter((r) => r && r.anrechenbar);
  const nonAnrCount = (rows || []).length - anrechenbareRows.length;

  // Iter 19.63.22 — Backend-authoritative Per-Zeile-Werte verwenden, damit
  // Tabellen-Summe EXAKT mit dem Final-Aggregat (Sidebar/„Anrechenbare
  // Dienstzeit gesamt") übereinstimmt.
  //
  // ALT (vor 19.63.22): effDays = rawDays × faktor → ROH-Wert OHNE
  // Overlap-Resolution. Bei Datensätzen mit überlappenden Beschäftigungen
  // (z.B. Martina Fall 2: laufende S.pura-geringfügig + spätere
  // Vollanstellungen) zeigte die Tabellen-Summe 5 J 9 M, das Final-Aggregat
  // aber 4 J 11 M — das verwirrte User massiv.
  //
  // NEU: rawDays bleibt als Pre-Overlap-Roh-Wert in einer eigenen Spalte
  // (Transparenz für den User), aber die „= Tage anr."-Spalte nutzt
  // dauer_anrechenbar_* vom Backend — das ist NACH Overlap-Resolution +
  // Cap-Anwendung. Bei reduzierten Zeilen wird das transparent gemacht.
  //
  // Iter 20.36.f10.3 (User-Feedback 2026-02-11) — Feinjustierung:
  // Für Cap-Affected Rows werden die Zeilen-Werte UND die SUM
  // konsistent auf Pre-Cap (post-overlap-pre-cap) gesetzt. Damit matcht
  // die Modal-Summe wieder mit „Brutto: X" im Tile (=
  // backendAggregate.post_overlap_pre_cap.totalDays). Der Global-Cap
  // wird erst in der Kaskade unten als eigener Schritt transparent
  // gemacht. Vorher lief die SUM über post-all-caps-Werte, was bei
  // realen Cap-Fällen zu Modal-Summe < Tile-Brutto führte.
  //
  // Math-Logik im Backend (_apply_overlap_resolution etc.) bleibt UNVERÄNDERT.
  const traceRows = anrechenbareRows.map((r, idx) => {
    const rawY = Number(r.dauer_jahre) || 0;
    const rawM = Number(r.dauer_monate) || 0;
    const rawT = Number(r.dauer_tage) || 0;
    const rawDays = rawY * 360 + rawM * 30 + rawT;
    const faktor = Number(r.anrechnungsfaktor) || 0;
    // Iter 19.63.33 — User-Spec: Im Modal soll die Zeilen-Spalte „= Tage anr."
    // die PRE-CAP-Werte zeigen (= Roh × Faktor × Overlap-Reduktion, aber OHNE
    // Cap-Reduktion). Cap wird erst in der Kaskade unten transparent.
    // Bei `_cap_zeroed=true` zeigt Backend `dauer_anrechenbar_*=0`; im Modal
    // rechnen wir den Pre-Cap-Wert (rawDays × faktor) dann lokal.
    // Für `_overlap_zeroed`-Items zeigen wir weiterhin 0 (= durch Überlappung
    // wirklich verloren, nicht durch Cap).
    const hasBackendValues = (
      typeof r.dauer_anrechenbar_jahre === "number" ||
      typeof r.dauer_anrechenbar_monate === "number" ||
      typeof r.dauer_anrechenbar_tage === "number"
    );
    let anrY, anrM, anrT, effDays;
    // Iter 19.63.35 / 20.36.f9 / 20.36.f10.3 — Cap-Affected-Erkennung
    // (Global-Cap sowie WT-Sub-Cap): Zeilen-Spalte UND SUM zeigen Pre-Cap
    // (post-overlap-pre-cap) — matcht das Tile-Brutto konsistent.
    const isCapAffected = (r._cap_zeroed || r._cap_reduced) && !r._overlap_zeroed;
    const isSubCapAffected = (r._wt_sub_cap_zeroed || r._wt_sub_cap_partial) && !r._overlap_zeroed;
    if (isCapAffected) {
      if (
        typeof r.dauer_anrechenbar_pre_cap_jahre === "number" ||
        typeof r.dauer_anrechenbar_pre_cap_monate === "number" ||
        typeof r.dauer_anrechenbar_pre_cap_tage === "number"
      ) {
        // Iter 20.36.f10.3 — Display + SUM beide auf Pre-Cap-Werte gesetzt.
        // Damit matcht Modal-Summe exakt mit Tile-Brutto (post_overlap_pre_cap).
        anrY = Number(r.dauer_anrechenbar_pre_cap_jahre) || 0;
        anrM = Number(r.dauer_anrechenbar_pre_cap_monate) || 0;
        anrT = Number(r.dauer_anrechenbar_pre_cap_tage) || 0;
        effDays = anrY * 360 + anrM * 30 + anrT;
      } else {
        // Fallback: alte FE-Math (sollte nicht mehr aufkommen).
        effDays = rawDays * faktor;
        anrY = Math.floor(effDays / 360);
        const anrRem = effDays - anrY * 360;
        anrM = Math.floor(anrRem / 30);
        anrT = anrRem - anrM * 30;
      }
    } else if (hasBackendValues) {
      // Iter 20.36.f9 — Bei WT-Sub-Cap-affected Rows zeigen wir für die
      // Row-Anzeige die post-sub-cap-Werte (kleinere Zahl); die SUM
      // aggregiert ebenfalls die post-sub-cap-Werte. Kein Unterschied
      // zur normalen post-cap-Anzeige — nur der visuelle Hinweis
      // im Modal, dass der Sub-Cap gegriffen hat.
      // Für non-cap-affected Items ist dauer_anrechenbar_* == post-overlap
      // (kein Global-Cap-Cut angewendet).
      anrY = Number(r.dauer_anrechenbar_jahre) || 0;
      anrM = Number(r.dauer_anrechenbar_monate) || 0;
      anrT = Number(r.dauer_anrechenbar_tage) || 0;
      effDays = anrY * 360 + anrM * 30 + anrT;
    } else {
      effDays = rawDays * faktor;
      anrY = Math.floor(effDays / 360);
      const anrRem = effDays - anrY * 360;
      anrM = Math.floor(anrRem / 30);
      anrT = anrRem - anrM * 30;
    }
    // Iter 19.63.22 — Reduktions-Diagnose: Wenn Backend-effDays signifikant
    // kleiner als rawDays × faktor ist (Toleranz 1 Tag für Rundung), wurde
    // die Zeile durch Overlap-Resolution oder Cap reduziert.
    // Iter 19.63.35 — Für cap-affected Items zeigen wir Pre-Cap-Werte (=
    // Post-Overlap × Faktor); die einzige verbleibende Reduktion ist Overlap.
    // Daher: isReduced nur dann, wenn der Wert NICHT vom Cap verursacht ist.
    const expectedPreOverlap = rawDays * faktor;
    const reductionDays = Math.max(0, expectedPreOverlap - effDays);
    const isReduced = reductionDays > 1.0;
    return {
      ...r, _idx: idx, rawY, rawM, rawT, rawDays, faktor,
      effDays, anrY, anrM, anrT,
      expectedPreOverlap, reductionDays, isReduced,
      isCapAffected, isSubCapAffected,
    };
  });

  const sumAnrDays = traceRows.reduce((acc, r) => acc + (r.effDays || 0), 0);

  // Iter 19.63.103 — Cap-Bypass-Items für eigene Modal-Sektion sammeln.
  // Wir zeigen die per-Row als Bypass markierten Vordienst-Items mit:
  //   • Arbeitgeber + Datumsspanne + Faktor
  //   • Lücke zur nächsten Position desselben AGs ODER zum Stichtag
  //   • Soft-Warning bei Lücke > 24 M. (KV-Grenzwert)
  // So sieht der Sachbearbeiter im Detail-Modal nachvollziehbar, warum
  // das Final-Aggregat den KV-Cap übersteigt.
  const bypassRows = (rows || [])
    .filter((r) => r && r.cap_bypass && r._cap_bypassed && r.anrechenbar)
    .map((r) => ({
      datum_von: r.datum_von || "",
      datum_bis: r.datum_bis || (r.was_laufend ? "laufend" : ""),
      arbeitgeber: r.arbeitgeber || "—",
      taetigkeit: r.taetigkeit || "—",
      faktor: typeof r.anrechnungsfaktor === "number" ? r.anrechnungsfaktor : 1.0,
      gap_months: r._bypass_gap_to_next_months,
      warning: r._bypass_chain_warning || null,
    }));
  const hasBypass = bypassRows.length > 0;
  const sameEmployerCfg = capBypassRules?.same_employer || null;
  const maxGapMonths = Number(sameEmployerCfg?.max_gap_months) || 24;

  // Iter 19.63.9 — Ausbildungs-Bonuszeiten in den Trace einbeziehen.
  // Trennen: nur anrechenbare Items zählen, und bypass_cap Items (Sonderregeln
  // wie KV-Handel „Kassakräfte +1 J.") werden SEPARAT addiert (über Cap hinaus).
  const ausbAnr = (ausbildungszeiten || []).filter((a) => a && a.anrechenbar && (Number(a.months) || 0) > 0);
  const ausbNormal = ausbAnr.filter((a) => !a.bypass_cap);
  const ausbBypass = ausbAnr.filter((a) => a.bypass_cap);
  const ausbNormalMonths = ausbNormal.reduce((s, a) => s + (Number(a.months) || 0), 0);
  const ausbBypassMonths = ausbBypass.reduce((s, a) => s + (Number(a.months) || 0), 0);
  // Normale Ausbildungs-Bonuszeiten → in Tage umrechnen (kaufm. 30/Monat) und zum Vordienst-Sum addieren.
  // Diese müssen den Cap respektieren — wir zeigen aber den Roh-Add und vertrauen,
  // dass Step 4 / `_apply_cap_to_items` die Cap-Logik nachgelagert anwendet.
  const ausbNormalDays = ausbNormalMonths * 30;
  const ausbBypassDays = ausbBypassMonths * 30;
  const capDays = (capMonths || 0) * 30;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-start justify-center bg-black/45 backdrop-blur-sm overflow-y-auto py-8 px-3"
      onClick={onClose}
      data-testid="berechnungsdetails-modal-backdrop"
    >
      <div
        className="bg-warm-50 max-w-4xl w-full shadow-2xl rounded-sm border border-warm-300"
        onClick={(e) => e.stopPropagation()}
        data-testid="berechnungsdetails-modal"
      >
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-warm-300 bg-warm-100">
          <div>
            <h2 className="font-cabinet text-xl text-ink-900 mb-0.5">
              Wie wird die anrechenbare Dienstzeit berechnet?
            </h2>
            <p className="text-[12px] text-warm-700 leading-snug">
              Pro Zeile wird die Bruttodauer im{" "}
              <strong>kaufmännischen 30/360-Verfahren</strong> ermittelt
              (Y × 360 + M × 30 + T), mit dem Anrechnungsfaktor multipliziert
              und am Ende auf Jahre/Monate/Tage zurückkaskadiert.
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="berechnungsdetails-close-btn"
            className="ml-4 p-1 rounded-sm hover:bg-warm-200 transition-colors"
            aria-label="Schließen"
          >
            <X size={18} weight="bold" className="text-warm-700" />
          </button>
        </div>

        {/* Per-row table */}
        <div className="px-6 py-4">
          <div className="text-[11px] uppercase tracking-widest font-semibold text-warm-600 mb-2">
            Pro Zeile
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[12px] border-collapse">
              <thead className="bg-warm-100 text-warm-700 uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="text-left px-2 py-2 font-medium">#</th>
                  <th className="text-left px-2 py-2 font-medium">Zeitraum</th>
                  <th className="text-left px-2 py-2 font-medium">Arbeitgeber</th>
                  <th className="text-right px-2 py-2 font-medium">Roh (30/360)</th>
                  <th
                    className="text-right px-2 py-2 font-medium"
                    title="Anrechnungsfaktor: 1,0 = voll · 0,5 = hälftig · 0,25 = ein Viertel · 0,0 = nicht anrechenbar"
                  >
                    × Faktor
                  </th>
                  <th className="text-right px-2 py-2 font-medium">= Tage anr.</th>
                </tr>
              </thead>
              <tbody>
                {traceRows.map((r, i) => (
                  <tr
                    key={i}
                    className={[
                      "border-b border-warm-200 hover:bg-warm-50",
                      r.was_laufend ? "bg-amber-50/40" : "",
                      r.isReduced ? "bg-orange-50/40" : "",
                    ].filter(Boolean).join(" ")}
                    data-testid={`berechnungsdetails-row-${i}`}
                  >
                    <td className="px-2 py-2 text-warm-500 tabular-nums">{i + 1}</td>
                    <td className="px-2 py-2 tabular-nums">
                      {_fmtDate(r.datum_von)}
                      {" – "}
                      {_fmtDate(r.datum_bis, Boolean(r.was_laufend))}
                      {r.was_laufend && (() => {
                        // Iter 19.63.24 — Stichtag-1-Datum berechnen (= echter
                        // letzter Tag der laufenden Vorbeschäftigung).
                        let stMinusOneLabel = "Stichtag − 1 Tag";
                        if (stichtag && /^\d{4}-\d{2}-\d{2}$/.test(String(stichtag).trim())) {
                          try {
                            const d = new Date(stichtag + "T00:00:00Z");
                            d.setUTCDate(d.getUTCDate() - 1);
                            const dd = String(d.getUTCDate()).padStart(2, "0");
                            const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
                            const yyyy = d.getUTCFullYear();
                            stMinusOneLabel = `${dd}.${mm}.${yyyy}`;
                          } catch (_e) { /* fallback */ }
                        }
                        return (
                          <div className="text-[10px] text-amber-700 font-normal italic mt-0.5">
                            (bis {stMinusOneLabel} berechnet — 1 Tag vor Stichtag)
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-2 py-2 text-warm-700 truncate max-w-[180px]">
                      {r.arbeitgeber || (
                        <span className="italic text-warm-400">— ohne AG —</span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums text-warm-700">
                      {/* Iter 19.63.27 — Authoritative Roh-Display vom Backend. */}
                      {r.display_raw
                        ? r.display_raw
                        : (
                          <>
                            {r.rawY ? `${r.rawY} J ` : ""}
                            {r.rawM ? `${r.rawM} M ` : ""}
                            {r.rawT ? `${r.rawT} T` : r.rawY || r.rawM ? "" : "0 T"}
                          </>
                        )}
                      <div className="text-[10px] text-warm-500">
                        = {r.rawDays} T
                      </div>
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums text-warm-700">
                      × {_fmtDE(r.faktor)}
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums font-medium text-brand-700">
                      {_fmtDE(r.effDays)} T
                      <div className="text-[10px] text-warm-500">
                        {/* Iter 19.63.27 — Authoritative Display-String vom Backend
                            falls verfügbar; sonst lokale Komposition als Fallback.
                            Iter 19.63.34/35 — Bei `_cap_zeroed` ODER `_cap_reduced`
                            zeigt das Modal den Pre-Cap-Wert
                            (Backend-`display_anrechenbar_pre_cap`), damit User
                            die Gesamtsumme nachvollziehen kann. */}
                        {r.isCapAffected && r.display_anrechenbar_pre_cap
                          ? r.display_anrechenbar_pre_cap
                          : (r.display_anrechenbar && !r.isCapAffected)
                            ? r.display_anrechenbar
                            : (
                              <>
                                {r.anrY ? `${r.anrY} J ` : ""}
                                {r.anrM ? `${r.anrM} M ` : ""}
                                {r.anrT ? `${_fmtDE(r.anrT)} T` : r.anrY || r.anrM ? "" : "0 T"}
                              </>
                            )}
                      </div>
                      {/* Iter 19.63.22 — Reduktions-Hinweis bei Overlap.
                          Iter 19.63.36 — Cap-Hinweis entfernt (Cap wird erst
                          unten in Final-Kaskade transparent). Stattdessen
                          zeigen wir auch bei cap-affected Items den normalen
                          Überlappungs-Hinweis, falls die Pre-Cap-Werte durch
                          Overlap reduziert wurden (= reductionDays > 1 T).
                          Iter 20.36.f14.14 — Bei WT-Sub-Cap-Reduktion (Non-WT-
                          Höchstgrenze der wt_kanzlei_kombiregel) darf der Text
                          NICHT als „Überlappung" gelabelt werden — dann ist es
                          die Cap-Reduktion, keine kalendarische Überschneidung. */}
                      {r.isSubCapAffected ? (
                        <div
                          className="text-[10px] text-amber-700 font-normal italic mt-0.5"
                          title="KV-Sonderregel: Höchstanrechnungsdauer für Zeiten außerhalb einer WT-Kanzlei"
                        >
                          {r.effDays < 0.5
                            ? "durch KV-Höchstgrenze außerhalb WT-Kanzlei auf 0 reduziert"
                            : `reduziert um ${_fmtDE(r.reductionDays)} T (KV-Höchstgrenze außerhalb WT-Kanzlei)`}
                        </div>
                      ) : r.isReduced && !r._overlap_zeroed && (
                        <div
                          className="text-[10px] text-orange-700 font-normal italic mt-0.5"
                          title={`Vor Überlappungs-Bereinigung: ${_fmtDE(r.expectedPreOverlap)} T · Abzug: ${_fmtDE(r.reductionDays)} T`}
                        >
                          {r.effDays < 0.5
                            ? "durch Überlappung auf 0 reduziert"
                            : `reduziert um ${_fmtDE(r.reductionDays)} T (Überlappung)`}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-brand-50 border-t-2 border-brand-300">
                  <td colSpan={5} className="px-2 py-2 text-right uppercase tracking-widest text-[10px] font-semibold text-warm-700">
                    Summe anrechenbare Vordienstzeiten
                  </td>
                  <td
                    className="px-2 py-2 text-right tabular-nums font-cabinet text-base text-brand-700"
                    data-testid="berechnungsdetails-sum-days"
                  >
                    {/* Iter 19.63.17 — User-Wunsch: Rundung sichtbar machen.
                        Zeigt nacheinander:
                          921,5 T  (Float-Summe)
                          = 2 J 6 M 21,5 T  (Kaskade des Floats)
                          aufgerundet: 2 J 6 M 22 T  (final mit Half-Up). */}
                    {(() => {
                      const sFloor = Math.floor(sumAnrDays / 360);
                      const sRem = sumAnrDays - sFloor * 360;
                      const sM = Math.floor(sRem / 30);
                      const sT = sRem - sM * 30; // can be float, e.g. 21.5
                      const isFractional = !Number.isInteger(sT);
                      const r = Math.floor(sumAnrDays + 0.5);
                      const rY = Math.floor(r / 360);
                      const rM = Math.floor((r % 360) / 30);
                      const rT = r % 30;
                      const ymdToStr = (y, m, t) => {
                        const parts = [];
                        if (y) parts.push(`${y} J.`);
                        if (m) parts.push(`${m} M.`);
                        // We always show T when float (e.g. 21,5 T) or when no Y/M.
                        if (t || (!y && !m)) parts.push(`${_fmtDE(t)} T.`);
                        return parts.join(" ");
                      };
                      return (
                        <>
                          <div>{_fmtDE(sumAnrDays)} T</div>
                          <div className="text-[11px] text-warm-600 font-normal mt-0.5">
                            = {ymdToStr(sFloor, sM, sT)}
                          </div>
                          {isFractional && (
                            <div className="text-[11px] text-warm-600 font-normal">
                              aufgerundet: <span className="font-medium text-brand-700">{ymdToStr(rY, rM, rT)}</span>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          {nonAnrCount > 0 && (
            <p className="text-[11px] text-warm-500 mt-3 italic">
              {nonAnrCount} weitere Zeile{nonAnrCount === 1 ? "" : "n"} sind nicht
              anrechenbar (Faktor 0,0) und tragen nichts zur Summe bei.
            </p>
          )}
        </div>

        {/* Iter 19.63.9 — Ausbildungs-Bonuszeiten + KV-Sonderregeln */}
        {ausbAnr.length > 0 && (
          <div className="px-6 py-4 border-t border-warm-300">
            <div className="text-[11px] uppercase tracking-widest font-semibold text-warm-600 mb-2">
              Ausbildungs-Bonuszeiten & KV-Sonderregeln
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[12px] border-collapse">
                <thead className="bg-warm-100 text-warm-700 uppercase tracking-wider text-[10px]">
                  <tr>
                    <th className="text-left px-2 py-2 font-medium">Bezeichnung</th>
                    <th className="text-right px-2 py-2 font-medium">Bonus</th>
                    <th className="text-left px-2 py-2 font-medium pl-3">Hinweis</th>
                  </tr>
                </thead>
                <tbody>
                  {ausbAnr.map((a, i) => {
                    const m = Number(a.months) || 0;
                    const d = m * 30;
                    return (
                      <tr
                        key={`ausb-${i}`}
                        className="border-b border-warm-200 hover:bg-warm-50"
                        data-testid={`berechnungsdetails-ausb-row-${i}`}
                      >
                        <td className="px-2 py-2 text-warm-800">{a.label || "—"}</td>
                        <td className="px-2 py-2 text-right tabular-nums font-medium text-brand-700">
                          + {_fmtMonthsYM(m)}
                          <div className="text-[10px] text-warm-500 font-normal">
                            (= {_fmtDE(d)} T)
                          </div>
                        </td>
                        <td className="px-2 py-2 pl-3 text-[11px] text-warm-600">
                          {a.bypass_cap ? (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-50 border border-amber-300 text-amber-800 rounded-sm">
                              KV-Sonderregel — wird ZUSÄTZLICH angerechnet (auch über Höchstgrenze hinaus)
                            </span>
                          ) : (
                            <span>Bonus-Anrechnung; respektiert die Höchstgrenze</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  {ausbNormalMonths > 0 && (
                    <tr className="bg-warm-50">
                      <td className="px-2 py-2 text-right uppercase tracking-widest text-[10px] font-semibold text-warm-600">
                        Summe normale Bonuszeiten
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums font-medium text-warm-700">
                        + {_fmtMonthsYM(ausbNormalMonths)}
                      </td>
                      <td className="px-2 py-2"></td>
                    </tr>
                  )}
                  {ausbBypassMonths > 0 && (
                    <tr className="bg-amber-50">
                      <td className="px-2 py-2 text-right uppercase tracking-widest text-[10px] font-semibold text-amber-800">
                        Summe KV-Sonderregeln (über Höchstgrenze)
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums font-medium text-amber-800">
                        + {_fmtMonthsYM(ausbBypassMonths)}
                      </td>
                      <td className="px-2 py-2"></td>
                    </tr>
                  )}
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {/* Iter 19.63.103 — Cap-Bypass „Selber AG"-Sektion.
            Nur sichtbar, wenn der User mindestens eine Vordienst-Zeile als
            „Selber Arbeitgeber (Bypass)" markiert hat. Zeigt jedes Bypass-
            Item mit Datumsspanne, gemessener Lücke (zur nächsten Position
            desselben AG bzw. zum Stichtag) und Warning falls Lücke > 24 M. */}
        {hasBypass && (
          <div className="px-6 py-4 border-t border-warm-300 bg-amber-50/30">
            <div className="text-[11px] uppercase tracking-widest font-semibold text-amber-800 mb-2 flex items-center gap-2">
              <span>Sonderregel &bdquo;Selber Arbeitgeber&ldquo;</span>
              <span className="text-[10px] font-normal normal-case tracking-normal text-amber-700">
                · {bypassRows.length} {bypassRows.length === 1 ? "Position" : "Positionen"} markiert
              </span>
            </div>
            <p className="text-[12px] text-warm-700 leading-snug mb-3">
              Diese Positionen wurden vom Sachbearbeiter als{" "}
              <strong>Beschäftigungen beim selben (= neuen) Arbeitgeber</strong>{" "}
              markiert. Sie zählen{" "}
              <strong>über die KV-Höchstanrechnungsdauer hinaus</strong>,
              sofern die zeitliche Lücke zur nächsten Position desselben
              Arbeitgebers (bzw. zum Stichtag bei der letzten Position)
              maximal <strong>{maxGapMonths} Monate</strong> beträgt.
            </p>
            <div className="overflow-x-auto -mx-2">
              <table className="w-full text-[12px] border-collapse">
                <thead>
                  <tr className="text-[10px] uppercase tracking-widest text-warm-600 border-b border-warm-300">
                    <th className="px-2 py-1 text-left font-medium">Zeitraum</th>
                    <th className="px-2 py-1 text-left font-medium">Arbeitgeber</th>
                    <th className="px-2 py-1 text-left font-medium">Tätigkeit</th>
                    <th className="px-2 py-1 text-right font-medium">Faktor</th>
                    <th className="px-2 py-1 text-right font-medium">Lücke</th>
                  </tr>
                </thead>
                <tbody>
                  {bypassRows.map((br, i) => {
                    const gapStr = typeof br.gap_months === "number"
                      ? `${br.gap_months.toFixed(1)} M.`
                      : "—";
                    const gapExceeded = typeof br.gap_months === "number" && br.gap_months > maxGapMonths;
                    return (
                      <tr
                        key={i}
                        className={gapExceeded ? "bg-amber-100/60" : "even:bg-amber-50/40"}
                        data-testid={`bypass-row-${i}`}
                      >
                        <td className="px-2 py-1.5 tabular-nums whitespace-nowrap text-warm-800">
                          {_fmtDate(br.datum_von)} – {br.datum_bis === "laufend" ? <em className="text-amber-700">laufend</em> : _fmtDate(br.datum_bis)}
                        </td>
                        <td className="px-2 py-1.5 text-warm-800">{br.arbeitgeber}</td>
                        <td className="px-2 py-1.5 text-warm-700">{br.taetigkeit}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums text-warm-800">
                          ×{br.faktor.toFixed(2).replace(/\.?0+$/, "")}
                        </td>
                        <td
                          className={[
                            "px-2 py-1.5 text-right tabular-nums",
                            gapExceeded ? "text-amber-800 font-semibold" : "text-warm-700",
                          ].join(" ")}
                          title={br.warning || `Lücke ≤ ${maxGapMonths} M. — KV-Sonderregel anwendbar`}
                        >
                          {gapStr}
                          {gapExceeded && " ⚠"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-amber-100/50 border-t border-amber-300">
                    <td colSpan={4} className="px-2 py-2 text-right uppercase tracking-widest text-[10px] font-semibold text-amber-800">
                      Summe Selber Arbeitgeber (über Höchstgrenze)
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums font-medium text-amber-800">
                      + {_fmtMonthsYM(Math.round((bypassVordienstTotalDays || 0) / 30))}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
            {bypassRows.some((b) => b.warning) && (
              <div className="mt-3 text-[11px] text-amber-800 italic leading-snug px-1 border-l-2 border-amber-400 pl-2" data-testid="bypass-warnings-list">
                <strong className="not-italic font-semibold">Hinweise:</strong>
                <ul className="list-disc list-inside mt-1 space-y-0.5">
                  {bypassRows
                    .filter((b) => b.warning)
                    .map((b, i) => (
                      <li key={i}>
                        <em className="font-normal">{b.arbeitgeber}</em>: {b.warning}
                      </li>
                    ))}
                </ul>
                <p className="mt-2 not-italic text-warm-700">
                  Sonderregel bleibt trotz Warnung aktiv — der Sachbearbeiter
                  entscheidet, ob die KV-Sonderregel in diesem Fall greift.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Cascade */}
        <div className="px-6 py-4 bg-warm-100 border-t border-warm-300">
          <div className="text-[11px] uppercase tracking-widest font-semibold text-warm-600 mb-2">
            Kaskade auf Jahre / Monate / Tage
          </div>
          <div className="space-y-2 text-[13px] text-warm-800 leading-relaxed">
            {/* Iter 19.63.11 — Neue Kaskaden-Reihenfolge:
                Vordienst → +Ausbildung → +Sonderregel → Höchstgrenze (nur Hinweis).
                Pro Schritt zeigen wir das laufende Y/M/T-Total (nicht nur Tage). */}
            {(() => {
              // Compute running totals.
              const step1Days = sumAnrDays;
              const r1 = Math.floor(step1Days + 0.5);
              const s1Y = Math.floor(r1 / 360);
              const s1M = Math.floor((r1 % 360) / 30);
              const s1T = r1 % 30;

              const step2Days = step1Days + ausbNormalDays;
              const r2 = Math.floor(step2Days + 0.5);
              const s2Y = Math.floor(r2 / 360);
              const s2M = Math.floor((r2 % 360) / 30);
              const s2T = r2 % 30;

              // Iter 19.63.103 — Vordienst-Bypass-Tage (= Σ aller per-Row
              // als „Selber AG" markierten Items). Verhält sich semantisch
              // identisch zu `ausbBypassDays` (Ausbildungs-Sonderregel-Bonus
              // wie Kassakräfte +1 J.): zählen ON TOP zur KV-Höchstanrechnung.
              // Werden bereits in `sumAnrDays` MITgezählt (Backend liefert
              // dauer_anrechenbar_* für Bypass-Items als Pre-Cap-Voll-Wert),
              // also in der Cascade NICHT erneut zur Step-1-Summe addieren —
              // statt dessen sauber separieren: nonBypassDays = capable durch
              // Cap, bypassDays = oberhalb Cap.
              const bypassVordienstDays = bypassVordienstTotalDays || 0;
              const nonBypassVordienstDays = Math.max(0, step1Days - bypassVordienstDays);
              const step2NonBypassDays = nonBypassVordienstDays + ausbNormalDays;
              const step3Days = step2NonBypassDays + bypassVordienstDays + ausbBypassDays;
              const r3 = Math.floor(step3Days + 0.5);
              const s3Y = Math.floor(r3 / 360);
              const s3M = Math.floor((r3 % 360) / 30);
              const s3T = r3 % 30;

              // Final: apply Höchstgrenze (capping). Bypass-Sonderregeln "leihen"
              // Höchstgrenze, normale Bonuszeiten und Vordienst werden gekappt.
              const cappedPreBypass = capDays > 0
                ? Math.min(step2NonBypassDays, capDays)
                : step2NonBypassDays;
              const finalDays = cappedPreBypass + bypassVordienstDays + ausbBypassDays;
              const rf = Math.floor(finalDays + 0.5);
              const fY = Math.floor(rf / 360);
              const fM = Math.floor((rf % 360) / 30);
              const fT = rf % 30;

              const capExceededHere = capDays > 0 && step2NonBypassDays > capDays;
              const sonderregelErhoehung = ausbBypassMonths > 0 || bypassVordienstDays > 0;
              const bypassVordienstMonthsApprox = Math.round(bypassVordienstDays / 30);
              const effectiveCapMonths = (capMonths || 0) + ausbBypassMonths + bypassVordienstMonthsApprox;

              return (
                <>
                  <div className="flex justify-between">
                    <span>Vordienstzeiten:</span>
                    <span className="tabular-nums font-medium">
                      {s1Y} J. {s1M} M. {_fmtDE(s1T)} T.
                    </span>
                  </div>
                  {/* Iter 19.63.103 — Wenn Vordienst-Bypass-Items existieren,
                      transparent aufschlüsseln: regulär (cap-pflichtig) +
                      Bypass-Anteil (über Cap hinaus). */}
                  {bypassVordienstDays > 0 && (
                    <>
                      <div className="flex justify-between text-warm-600 text-[12px] pl-4">
                        <span>davon regulär (cap-pflichtig):</span>
                        <span className="tabular-nums">
                          {Math.floor(nonBypassVordienstDays / 360)} J.{" "}
                          {Math.floor((nonBypassVordienstDays % 360) / 30)} M.{" "}
                          {_fmtDE(Math.floor(nonBypassVordienstDays % 30))} T.
                        </span>
                      </div>
                      <div className="flex justify-between text-amber-700 text-[12px] pl-4">
                        <span>davon &bdquo;Selber Arbeitgeber&ldquo; (über Höchstgrenze):</span>
                        <span className="tabular-nums">
                          + {_fmtMonthsYM(bypassVordienstMonthsApprox)}
                        </span>
                      </div>
                    </>
                  )}
                  {ausbNormalDays > 0 && (
                    <>
                      <div className="flex justify-between text-warm-700">
                        <span>+ Ausbildungs-Bonuszeiten:</span>
                        <span className="tabular-nums">+ {_fmtMonthsYM(ausbNormalMonths)}</span>
                      </div>
                      <div className="flex justify-between text-warm-700 font-medium">
                        <span>= Zwischensumme:</span>
                        <span className="tabular-nums">{s2Y} J. {s2M} M. {_fmtDE(s2T)} T.</span>
                      </div>
                    </>
                  )}
                  {ausbBypassDays > 0 && (
                    <>
                      <div className="flex justify-between text-amber-800">
                        <span>+ KV-Sonderregel Ausbildung (über Höchstgrenze):</span>
                        <span className="tabular-nums">+ {_fmtMonthsYM(ausbBypassMonths)}</span>
                      </div>
                      <div className="flex justify-between text-warm-700 font-medium">
                        <span>= Zwischensumme:</span>
                        <span className="tabular-nums">{s3Y} J. {s3M} M. {_fmtDE(s3T)} T.</span>
                      </div>
                    </>
                  )}
                  {/* Iter 19.63.12 — Höchstgrenze-Zeile vereinfacht:
                      Kein Tage-Abzug mehr. Wenn der Cap greift, wird das nur
                      als Hinweis UNTER der Final-Summe gezeigt. */}
                  {capMonths > 0 && (
                    <div className={`flex justify-between ${capExceededHere ? "text-amber-700" : "text-warm-600"}`}>
                      <span>
                        Höchstgrenze (lt. KV):{" "}
                        {sonderregelErhoehung ? (
                          <>
                            <span className="line-through text-warm-400">{capMonths / 12} J.</span>
                            {" → "}
                            <strong className="text-amber-700">{Math.round(effectiveCapMonths / 12)} J.</strong>
                            {" "}<span className="text-[11px]">(durch Sonderregel erhöht)</span>
                          </>
                        ) : (
                          <strong>{Math.round(capMonths / 12)} J.</strong>
                        )}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between border-t border-warm-300 pt-2 mt-2 font-cabinet text-base">
                    <span>Anrechenbare Dienstzeit gesamt:</span>
                    <span
                      className="tabular-nums text-brand-700 font-medium"
                      data-testid="berechnungsdetails-final"
                    >
                      {aggregate?.display || `${fY} J. ${fM} M. ${fT} T.`}
                    </span>
                  </div>
                  {/* Iter 19.63.12 — Hinweis unter Final-Summe wenn Höchstgrenze greift. */}
                  {capMonths > 0 && capExceededHere && (
                    <div
                      className="text-[11px] text-amber-700 -mt-1 text-right italic"
                      data-testid="berechnungsdetails-cap-hint"
                    >
                      {sonderregelErhoehung
                        ? `Höchstgrenze erreicht (${Math.round(capMonths / 12)} J. lt. KV` +
                          (ausbBypassMonths > 0 ? ` + ${Math.round(ausbBypassMonths / 12)} J. Ausb.-Sonderregel` : "") +
                          (bypassVordienstDays > 0 ? ` + ${_fmtMonthsYM(bypassVordienstMonthsApprox)} „Selber Arbeitgeber"` : "") +
                          ")"
                        : `Höchstgrenze erreicht (${Math.round(capMonths / 12)} J. lt. KV)`}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
          {stichtag && (
            <p className="text-[11px] text-warm-500 mt-3">
              Stichtag der Berechnung: <strong>{_fmtDate(stichtag)}</strong>.
              Laufende Beschäftigungen werden bis zu diesem Datum angerechnet.
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-warm-300 flex justify-end bg-warm-50">
          <button
            onClick={onClose}
            data-testid="berechnungsdetails-close-footer-btn"
            className="px-4 py-1.5 bg-warm-800 text-warm-50 text-[12px] uppercase tracking-widest font-medium hover:bg-ink-900 transition-colors rounded-sm"
          >
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
}
